// ─────────────────────────────────────────────────────────────
// widgets.jsx — Dashboard widgets, skeleton loaders, pull-to-refresh.
// ES-Module: importiert von theme.jsx + modal-utils.jsx, exportiert alles.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from 'react';
import { F, ACCENTS, T, haptic, Icon, StatusIcon } from './theme.jsx';
import { useModalViewport, modalShellStyle } from './modal-utils.jsx';

// ─────────────────────────────────────────────────────────────
// Global Keyframes-Injection (P-Sheets-Skeleton-Fix)
// App() rendert seinen <style>-Block nur im "app"-screen — auf der Landing,
// in Trödel-/Markt-Hub fehlten die Keyframes. Wir injizieren sie hier global
// einmalig beim widgets.jsx-Modul-Load, damit Skeleton-Shimmer + pulse-Dots
// + Sheet-Animationen überall laufen.
// ─────────────────────────────────────────────────────────────
(function injectGlobalKeyframes() {
  if (typeof document === "undefined") return;
  const STYLE_ID = "__memorica_global_keyframes__";
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes skeletonPulse { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes pulse    { 0%,100%{transform:scale(1);opacity:.6}50%{transform:scale(1.5);opacity:0} }
    @keyframes breathe  { 0%,100%{transform:scale(0.9);opacity:0.5}50%{transform:scale(1.15);opacity:1} }
    @keyframes fadeIn   { from{opacity:0}to{opacity:1} }
    @keyframes slideUp  { from{transform:translateX(-50%) translateY(100%)}to{transform:translateX(-50%) translateY(0)} }
    @keyframes slideInRight { from{opacity:0;transform:translate3d(20px,0,0)}to{opacity:1;transform:translate3d(0,0,0)} }
    @keyframes toastIn  { from{opacity:0;transform:translate3d(0,-12px,0)}to{opacity:1;transform:translate3d(0,0,0)} }
    @keyframes modalIn  { from{opacity:0;transform:translate3d(-50%,16px,0)}to{opacity:1;transform:translate3d(-50%,0,0)} }
    @keyframes ptrSpin  { 0%{transform:rotate(0deg)}100%{transform:rotate(360deg)} }
  `;
  document.head.appendChild(style);
})();

// ─────────────────────────────────────────────────────────────
// Pull-to-refresh hook für scrollbare Container.
// Triggert `onRefresh` wenn der User von ganz oben nach unten zieht (>= threshold).
// ─────────────────────────────────────────────────────────────
function usePullToRefresh(onRefresh, threshold = 70) {
  const containerRef = useRef(null);
  const startY = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleStart = (e) => {
      if (el.scrollTop > 0) { startY.current = 0; return; }
      startY.current = e.touches ? e.touches[0].clientY : e.clientY;
    };
    const handleMove = (e) => {
      if (!startY.current) return;
      if (refreshing) return;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const dy = y - startY.current;
      if (dy <= 0) { setPullDistance(0); return; }
      const resisted = Math.min(120, dy * 0.55);
      setPullDistance(resisted);
    };
    const handleEnd = async () => {
      if (!startY.current) return;
      if (pullDistance >= threshold && !refreshing) {
        setRefreshing(true);
        haptic(15);
        try { await onRefresh?.(); } catch (e) { console.warn(e); }
        setRefreshing(false);
      }
      startY.current = 0;
      setPullDistance(0);
    };

    el.addEventListener("touchstart", handleStart, { passive: true });
    el.addEventListener("touchmove",  handleMove,  { passive: true });
    el.addEventListener("touchend",   handleEnd);
    el.addEventListener("touchcancel",handleEnd);
    return () => {
      el.removeEventListener("touchstart", handleStart);
      el.removeEventListener("touchmove",  handleMove);
      el.removeEventListener("touchend",   handleEnd);
      el.removeEventListener("touchcancel",handleEnd);
    };
  }, [onRefresh, threshold, pullDistance, refreshing]);

  return { containerRef, pullDistance, refreshing };
}

function PullToRefreshIndicator({ pullDistance, refreshing, threshold = 70, t }) {
  const ready = pullDistance >= threshold;
  const opacity = Math.min(1, pullDistance / 30);
  const scale = Math.min(1, 0.6 + pullDistance / 100);
  if (pullDistance === 0 && !refreshing) return null;
  return (
    <div style={{
      position: "absolute", top: 0, left: 0, right: 0,
      display: "flex", justifyContent: "center", alignItems: "center",
      paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
      pointerEvents: "none",
      transform: `translateY(${refreshing ? threshold : pullDistance}px)`,
      opacity,
      transition: refreshing ? "transform 0.2s ease" : "none",
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 15,
        background: t?.glass || "rgba(255,255,255,0.7)",
        border: `1px solid ${t?.glassBorder || "rgba(0,0,0,0.06)"}`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
        transform: `scale(${scale})`,
      }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{
          animation: refreshing ? "ptrSpin 0.7s linear infinite" : "none",
          color: t?.accent?.blue?.solid || "#0A84FF",
          transform: refreshing ? "none" : `rotate(${ready ? 180 : 0}deg)`,
          transition: "transform 0.2s ease",
        }}>
          <path d="M3 7L7 3L11 7M7 3V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Skeleton loaders — Pulse-Blocks für Lade-Phasen.
// ─────────────────────────────────────────────────────────────
function Skeleton({ width = "100%", height = 14, t, dark, radius = 6, style }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: t?.skeletonBg || (dark ? "rgba(255,255,255,0.05)" : "rgba(60,60,67,0.06)"),
      backgroundImage: `linear-gradient(90deg, transparent, ${t?.skeletonHi || (dark ? "rgba(255,255,255,0.1)" : "rgba(60,60,67,0.12)")}, transparent)`,
      backgroundSize: "200% 100%",
      backgroundRepeat: "no-repeat",
      animation: "skeletonPulse 1.4s ease-in-out infinite",
      ...style,
    }} />
  );
}

function SkeletonOrderRow({ t, dark }) {
  return (
    <div style={{
      padding: "14px 16px", display: "flex", alignItems: "center", gap: 12,
      borderBottom: `0.5px solid ${t.divider}`,
    }}>
      <Skeleton width={36} height={36} radius={18} t={t} dark={dark} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <Skeleton width="55%" height={13} t={t} dark={dark} />
        <Skeleton width="35%" height={10} t={t} dark={dark} />
      </div>
      <Skeleton width={50} height={20} radius={10} t={t} dark={dark} />
    </div>
  );
}

function SkeletonWidget({ t, dark, glass, hero }) {
  const glassFn = glass || ((extra = {}) => ({
    background: t?.glass || (dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)"),
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
    border: `1px solid ${t?.glassBorder || "rgba(0,0,0,0.06)"}`,
    boxShadow: t?.glassShadow || "0 1px 3px rgba(0,0,0,0.05)",
    ...extra,
  }));
  return (
    <div style={{
      ...glassFn({ borderRadius: 20 }),
      padding: hero ? "14px 16px" : "12px 14px",
      minHeight: hero ? 76 : 104,
      display: "flex", flexDirection: "column", gap: 8, justifyContent: "center",
    }}>
      <Skeleton width="38%" height={9} t={t} dark={dark} />
      <Skeleton width="55%" height={hero ? 22 : 18} t={t} dark={dark} />
      {!hero && <Skeleton width="80%" height={8} t={t} dark={dark} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SkeletonText / SkeletonListItem (P-Sheets-Skeleton-Refactor)
// Wrapper um <Skeleton> mit semantischen Defaults.
// ─────────────────────────────────────────────────────────────
function SkeletonText({ width = "60%", height = 12, t, dark, style }) {
  return <Skeleton width={width} height={height} radius={4} t={t} dark={dark} style={style} />;
}

// SkeletonListItem — Modus-Icon links + 2 Text-Zeilen + Chevron-Bar.
// Passt für die Bereiche-Liste auf der Landing + Markt-Tage-Listen.
function SkeletonListItem({ t, dark, withIcon = true, withChevron = true }) {
  return (
    <div style={{
      padding: "14px 16px", display: "flex", alignItems: "center", gap: 14,
    }}>
      {withIcon && <Skeleton width={36} height={36} radius={10} t={t} dark={dark} />}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <Skeleton width="50%" height={14} radius={4} t={t} dark={dark} />
        <Skeleton width="35%" height={11} radius={4} t={t} dark={dark} />
      </div>
      {withChevron && <Skeleton width={10} height={16} radius={2} t={t} dark={dark} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// useDelayedSkeleton (P-Sheets-Skeleton-Refactor)
// Liefert true erst nach `delay`ms wenn `loading` weiterhin true ist.
// Ergebnis: bei sehr schnellem Load (<200ms) wird kein Skeleton gezeigt
// → kein Flicker. Sobald loading=false wird, sofort false.
// ─────────────────────────────────────────────────────────────
function useDelayedSkeleton(loading, delay = 200) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!loading) { setShow(false); return; }
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [loading, delay]);
  return show;
}

// ═════════════════════════════════════════════════════════════
// BottomSheet (P-Sheets-Skeleton-Refactor)
// Apple-Style Full-Width Bottom-Sheet mit:
//   • Slide-In von unten (Spring-Animation)
//   • Drag-to-Dismiss am Header (nicht am Body — Body scrollt normal)
//   • Threshold: distance > 30% Sheet-Höhe ODER velocity > 500 px/s
//   • Drag-Handle (Pill) oben + Title + X-Button
//   • Background-Overlay 36% Opacity, fade-in/out
//   • Auf Desktop max-width 600px, zentriert; auf Mobile full-width
//
// API:
//   <BottomSheet isOpen={...} onClose={...} title="..." showCloseButton={true}
//                dark={dark} t={t} F={F}
//                heightHint="auto"|"large" — Hint für initial sheet height>
//     {content}
//   </BottomSheet>
// ═════════════════════════════════════════════════════════════
function BottomSheet({ isOpen, onClose, title, subtitle, showCloseButton = true, children, dark, t, F, heightHint = "large", size, stickyFooter }) {
  // size: "medium" (75dvh, snappable to full) | "large" (full height, safe-area)
  // Backward compat: if size not provided, derive from heightHint
  const resolvedSize = size || (heightHint === "large" ? "large" : "medium");

  const [mounted, setMounted] = useState(isOpen);
  const [animating, setAnimating] = useState(false);
  const [dragOffset, setDragOffset] = useState(0); // px from current detent
  const [detent, setDetent] = useState("default"); // "default" | "full"
  const [isClosing, setIsClosing] = useState(false);
  const [kbBottom, setKbBottom] = useState(0); // px lift for keyboard avoidance
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, samples: [], pointerId: null, sheetH: 0 });

  // Mount/Unmount animation
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setIsClosing(false);
      setDetent("default");
      setDragOffset(0);
      // RAF to allow initial render at translateY(100%) before animating in
      requestAnimationFrame(() => setAnimating(true));
    } else if (mounted) {
      setAnimating(false);
      setIsClosing(true);
      const timer = setTimeout(() => {
        setMounted(false);
        setIsClosing(false);
        setDragOffset(0);
        setDetent("default");
      }, 280);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  // Body scroll lock
  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mounted]);

  // Keyboard avoidance via visualViewport
  useEffect(() => {
    if (!mounted) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const kbHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbBottom(kbHeight);
    };
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, [mounted]);

  // Auto-scroll focused input into view when keyboard opens
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const onFocusIn = (e) => {
      const target = e.target;
      if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") return;
      setTimeout(() => {
        target.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 150);
    };
    el.addEventListener("focusin", onFocusIn);
    return () => el.removeEventListener("focusin", onFocusIn);
  }, [mounted]);

  // Drag handlers — only on header
  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const sheet = headerRef.current?.parentElement;
    dragRef.current = {
      active: true,
      startY: e.clientY,
      samples: [{ t: performance.now(), y: e.clientY }],
      pointerId: e.pointerId,
      sheetH: sheet?.getBoundingClientRect().height || window.innerHeight * 0.75,
    };
    try { headerRef.current?.setPointerCapture?.(e.pointerId); } catch {}
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active || d.pointerId !== e.pointerId) return;
    const dy = e.clientY - d.startY;
    d.samples.push({ t: performance.now(), y: e.clientY });
    if (d.samples.length > 6) d.samples.shift();
    if (resolvedSize === "medium" && detent === "default") {
      // allow both upward (negative) and downward drag
      setDragOffset(dy);
    } else {
      // large or medium-full: only downward drag
      if (dy < 0) { setDragOffset(0); return; }
      setDragOffset(dy);
    }
  };

  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    d.active = false;
    try { headerRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
    const now = performance.now();
    const recent = d.samples.filter(s => now - s.t < 140);
    let velocity = 0; // px/s, positive = downward
    if (recent.length >= 2) {
      const dy = recent[recent.length - 1].y - recent[0].y;
      const dt = recent[recent.length - 1].t - recent[0].t;
      if (dt > 0) velocity = (dy / dt) * 1000;
    }

    if (resolvedSize === "medium" && detent === "default") {
      if (dragOffset < -40 || velocity < -300) {
        // fast upward swipe or upward drag → expand to full
        setDetent("full");
        setDragOffset(0);
      } else if (dragOffset > d.sheetH * 0.3 || velocity > 500) {
        // downward past threshold → dismiss
        setDragOffset(0);
        onClose();
      } else {
        setDragOffset(0); // spring back to default
      }
    } else if (resolvedSize === "medium" && detent === "full") {
      if (velocity > 800 || dragOffset > d.sheetH * 0.55) {
        // very fast or very far → dismiss entirely
        setDragOffset(0);
        onClose();
      } else if (velocity > 300 || dragOffset > d.sheetH * 0.25) {
        // moderate → snap back to default detent
        setDetent("default");
        setDragOffset(0);
      } else {
        setDragOffset(0); // spring back to full
      }
    } else {
      // large sheet: downward drag → dismiss
      if (dragOffset > d.sheetH * 0.3 || velocity > 500) {
        setDragOffset(0);
        onClose();
      } else {
        setDragOffset(0);
      }
    }
  };

  if (!mounted) return null;

  // translateY calculation — transform-only approach:
  //   off-screen:              100%
  //   large / medium-full:     ${dragOffset}px   (0 = at top of safe area)
  //   medium-default:          calc(25dvh + ${dragOffset}px)
  //   closing:                 100%
  let translateY = "100%";
  if (animating && !isClosing) {
    if (resolvedSize === "medium" && detent === "default") {
      translateY = dragOffset !== 0 ? `calc(25dvh + ${dragOffset}px)` : "25dvh";
    } else {
      translateY = `${dragOffset}px`;
    }
  }
  if (isClosing) translateY = "100%";

  const isDragging = dragOffset !== 0 && !isClosing;
  const springCurve = "cubic-bezier(0.32, 0.72, 0, 1)";
  const transition = isDragging
    ? "none"
    : `transform 0.32s ${springCurve}, bottom 0.22s ${springCurve}`;

  // Overlay dims proportionally to drag distance
  const dimBase = resolvedSize === "medium" && detent === "default"
    ? window.innerHeight * 0.35
    : window.innerHeight * 0.6;
  const overlayOpacity = isClosing ? 0 : !animating ? 0
    : Math.max(0, 1 - (Math.max(0, dragOffset) / dimBase));

  // top + bottom constraints: CSS auto-computes height, clamping sheet between
  // notch and keyboard. No JS math needed for safe-area.
  const sheetTop = "max(env(safe-area-inset-top, 0px), 8px)";

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 92,
          background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.36)",
          backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)",
          opacity: overlayOpacity,
          transition: isDragging ? "none" : `opacity 0.28s ${springCurve}`,
          pointerEvents: animating && !isClosing ? "auto" : "none",
        }}
      />
      {/* Sheet — top+bottom pins prevent notch overflow when keyboard lifts */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: "fixed",
          left: 0, right: 0,
          top: sheetTop,   // never above notch
          bottom: kbBottom, // lifts above keyboard, CSS computes height
          zIndex: 93,
          margin: "0 auto",
          maxWidth: 600,
          background: dark ? "#0d1117" : "#f2f2f7",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          display: "flex", flexDirection: "column",
          transform: `translateY(${translateY})`,
          transition,
          willChange: isDragging ? "transform" : "auto",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
          fontFamily: F,
          overflow: "hidden",
        }}
      >
        {/* Header (Drag-Zone) */}
        <div
          ref={headerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            paddingTop: 8, paddingBottom: 14,
            paddingLeft: 16, paddingRight: 16,
            cursor: "grab",
            touchAction: "none",
            borderBottom: `0.5px solid ${t.divider}`,
            position: "relative",
            background: dark ? "#0d1117" : "#f2f2f7",
            flexShrink: 0,
          }}
        >
          {/* Drag-Handle */}
          <div style={{
            width: 36, height: 5, borderRadius: 3,
            background: dark ? "rgba(255,255,255,0.22)" : "rgba(60,60,67,0.28)",
            margin: "0 auto 12px",
          }}/>
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative", minHeight: 24 }}>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{
                fontSize: 17, fontWeight: 600, color: t.text,
                letterSpacing: "-0.3px",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>{title}</div>
              {subtitle && (
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtitle}</div>
              )}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                aria-label="Schließen"
                onPointerDown={e => e.stopPropagation()}
                style={{
                  position: "absolute", right: 0, top: 0,
                  width: 30, height: 30, borderRadius: 15, padding: 0,
                  background: dark ? "rgba(118,118,128,0.32)" : "rgba(118,118,128,0.18)",
                  border: "none", cursor: "pointer",
                  color: dark ? "rgba(235,235,245,0.7)" : "rgba(60,60,67,0.6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        {/* Scrollable Content */}
        <div ref={contentRef} style={{
          flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
          paddingBottom: stickyFooter ? 0 : "calc(env(safe-area-inset-bottom, 16px) + 16px)",
        }}>
          {children}
        </div>
        {/* Sticky Footer */}
        {stickyFooter && (
          <div style={{
            flexShrink: 0,
            paddingBottom: "calc(env(safe-area-inset-bottom, 16px) + 8px)",
            paddingLeft: 16, paddingRight: 16,
            paddingTop: 12,
            borderTop: `0.5px solid ${t.divider}`,
            background: dark ? "#0d1117" : "#f2f2f7",
          }}>
            {stickyFooter}
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Widget building blocks — WidgetCard + WidgetHeader.
// ─────────────────────────────────────────────────────────────
function WidgetCard({ children, onClick, t, dark, F, size, gridSpan, animationDelay = 0, glass, variant }) {
  const isClickable = typeof onClick === "function";
  const glassFn = glass || ((extra = {}) => ({
    background: t?.glass || (dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)"),
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
    border: `1px solid ${t?.glassBorder || "rgba(0,0,0,0.06)"}`,
    boxShadow: t?.glassShadow || "0 1px 3px rgba(0,0,0,0.05)",
    ...extra,
  }));
  const isHero    = variant === "hero";
  const isCompact = variant === "compact";
  const padX = isHero ? 16 : isCompact ? 12 : 14;
  const padY = isHero ? 14 : isCompact ? 10 : 12;
  const minH = isHero ? 76 : isCompact ? 56 : (size === "small" ? 88 : 104);
  return (
    <div
      onClick={onClick}
      style={{
        gridColumn: gridSpan,
        ...glassFn({ borderRadius: 20 }),
        padding: `${padY}px ${padX}px`,
        cursor: isClickable ? "pointer" : "default",
        transition: "transform 0.18s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.22s ease",
        animation: `widgetIn 0.38s cubic-bezier(0.34, 1.2, 0.64, 1) backwards`,
        animationDelay: `${animationDelay}s`,
        minHeight: minH,
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}
      onMouseEnter={isClickable ? (e) => { e.currentTarget.style.boxShadow = `0 6px 22px ${dark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.07)"}`; } : undefined}
      onMouseLeave={isClickable ? (e) => { e.currentTarget.style.boxShadow = ""; } : undefined}
      onPointerDown={isClickable ? (e) => { e.currentTarget.style.transform = "scale(0.975)"; } : undefined}
      onPointerUp={isClickable ? (e) => { e.currentTarget.style.transform = ""; } : undefined}
      onPointerLeave={isClickable ? (e) => { e.currentTarget.style.transform = ""; } : undefined}
    >
      {children}
    </div>
  );
}

function WidgetHeader({ icon, title, t, F, accent }) {
  let iconNode = null;
  if (icon) {
    if (typeof icon === "function") iconNode = icon(12);
    else if (typeof icon === "string") iconNode = <span style={{ fontSize: 11, lineHeight: 1, opacity: 0.85 }}>{icon}</span>;
    else iconNode = icon;
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
      {iconNode && <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: accent || t.textMuted,
        opacity: accent ? 1 : 0.7,
      }}>{iconNode}</span>}
      <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: F }}>{title}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Widget components — tiny, self-contained, fed by props from DashboardArea.
// ─────────────────────────────────────────────────────────────
function TodaySummaryWidget(props) {
  const { orders, t, F, can, getOrderRevenue, fmt2, onOpenScanner, onSwitchToOrders, onShowAllOrders } = props;
  const today = new Date().toISOString().slice(0, 10);
  const todayOrders = orders.filter(o => o.datum === today && !o.archived);
  const todayRev = todayOrders.filter(o => o.status !== "Retoure").reduce((s, o) => s + (getOrderRevenue?.(o) || 0), 0);
  const bereitCount = orders.filter(o => o.status === "Bereit" && !o.archived).length;
  const offenCount  = orders.filter(o => o.status === "Offen"  && !o.archived).length;
  // Überfällige Bestellungen (> 12h offen / bereit) → diskreter Hinweis im Hero,
  // statt eigenes Widget. Tap führt zur Liste damit der User direkt handeln kann.
  const stale = computeStaleOrders(orders);
  const staleCount = stale.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
          <div style={{ fontSize: 34, fontWeight: 700, color: t.text, letterSpacing: "-0.9px", lineHeight: 1, fontFamily: F, fontVariantNumeric: "tabular-nums" }}>{todayOrders.length}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: F }}>Heute</div>
            <div style={{ fontSize: 11, color: t.textSub, fontFamily: F, lineHeight: 1.1, whiteSpace: "nowrap" }}>
              {todayOrders.length === 1 ? "Bestellung" : "Bestellungen"}
            </div>
          </div>
        </div>
        {can?.("finance") && (
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.text, letterSpacing: "-0.4px", lineHeight: 1, fontVariantNumeric: "tabular-nums", fontFamily: F }}>{fmt2(todayRev)} €</div>
            <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontFamily: F, letterSpacing: "0.04em" }}>Umsatz</div>
          </div>
        )}
      </div>

      {/* Action-Banner — fokussiert auf nur EINE Information pro Banner-Variante.
          Offen- und Bereit-Counts haben jetzt ihre eigene Anzeige in der Bottom-Nav
          (Badges am Bestellungen- und Scanner-Symbol), daher hier nicht mehr.
          Priorität:
          1. staleCount > 0 → roter Versand-überfällig-Banner (NUR diese Info)
          2. bereitCount > 0 → lila Scanner-CTA
          3. alles 0 → grüner "Alles versendet"
          (Reine Offen ohne Stale → kein Banner; Bottom-Nav-Badge reicht.) */}
      {staleCount > 0 ? (
        <button
          onClick={(e) => { e.stopPropagation(); (onShowAllOrders || onSwitchToOrders)?.(); haptic(8); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            background: t.accent.red.weak,
            border: `1px solid ${t.accent.red.solid}40`,
            borderRadius: 12, padding: "9px 12px",
            cursor: "pointer", fontFamily: F,
            transition: "transform 0.15s ease",
          }}
          onPointerDown={e => e.currentTarget.style.transform = "scale(0.98)"}
          onPointerUp={e => e.currentTarget.style.transform = ""}
          onPointerLeave={e => e.currentTarget.style.transform = ""}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ flexShrink: 0, color: t.accent.red.solid, display: "inline-flex" }}>{Icon.warning(13)}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.accent.red.solid, letterSpacing: "-0.1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {staleCount} {staleCount === 1 ? "Bestellung verspätet" : "Bestellungen verspätet"}
            </span>
          </span>
          <span style={{ display: "inline-flex", color: t.accent.red.solid, opacity: 0.8, flexShrink: 0 }}>{Icon.arrow(12)}</span>
        </button>
      ) : bereitCount > 0 ? (
        // Pakete sind versandbereit → lila Scanner-CTA für Versand-Trip.
        <button
          onClick={(e) => { e.stopPropagation(); onOpenScanner?.(); haptic(10); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            background: `linear-gradient(135deg, ${t.accent.purple.solid}, ${t.accent.blue.solid})`,
            border: "none", borderRadius: 12, padding: "10px 14px",
            cursor: "pointer", fontFamily: F, color: "#fff",
            boxShadow: `0 3px 14px ${t.accent.purple.weak}`,
            transition: "transform 0.15s ease",
          }}
          onPointerDown={e => e.currentTarget.style.transform = "scale(0.97)"}
          onPointerUp={e => e.currentTarget.style.transform = ""}
          onPointerLeave={e => e.currentTarget.style.transform = ""}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 22, height: 22, borderRadius: 11, background: "rgba(255,255,255,0.22)",
            }}>{Icon.scanner(13)}</span>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.1px" }}>
              {bereitCount} {bereitCount === 1 ? "Paket versenden" : "Pakete versenden"}
            </span>
          </span>
          <span style={{ display: "inline-flex" }}>{Icon.arrow(13)}</span>
        </button>
      ) : offenCount === 0 ? (
        // Wirklich alles versendet (nichts offen, nichts bereit, nichts verspätet).
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          background: t.accent.green.weak, color: t.accent.green.solid,
          padding: "6px 11px", borderRadius: 10,
          fontSize: 11, fontWeight: 600, fontFamily: F,
          alignSelf: "flex-start",
        }}>
          {StatusIcon.dotCheck(12, t.accent.green.solid)}
          <span>Alles versendet</span>
        </div>
      ) : null /* Nur Offen ohne Stale → kein Banner; Bottom-Nav-Badge zeigt's */}
    </div>
  );
}

function BereitCountWidget(props) {
  const { orders, t, F } = props;
  const count = orders.filter(o => o.status === "Bereit" && !o.archived).length;
  return (
    <>
      <WidgetHeader icon={Icon.box} title="Bereit" t={t} F={F} accent={count > 0 ? t.accent.purple.solid : undefined} />
      <div>
        <div style={{ fontSize: 32, fontWeight: 700, color: count > 0 ? "#BF5AF2" : t.textMuted, letterSpacing: "-0.9px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{count}</div>
        <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontFamily: F }}>{count === 0 ? "alle versendet" : "zu versenden"}</div>
      </div>
    </>
  );
}

function ReorderAlarmsWidget(props) {
  const { alarms, t, F, getChannelLabel } = props;
  // Top-Alarm priorisieren: "out"-Level zuerst (kritischer), dann low.
  const sorted = alarms.slice().sort((a, b) => {
    if (a.level === b.level) return (a.stock || 0) - (b.stock || 0);
    return a.level === "out" ? -1 : 1;
  });
  const top = sorted[0];
  const rest = Math.max(0, sorted.length - 1);
  if (!top) return null;
  const isOut = top.level === "out";
  return (
    <>
      <WidgetHeader icon={Icon.warning} title="Nachbestellen" t={t} F={F} accent={t.accent.orange.solid} />
      {/* Kompakt: 2 Zeilen zeigen welcher Batch & welcher Kanal kritisch ist + Stock-Pille */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 13, color: t.text, fontWeight: 600, fontFamily: F,
            letterSpacing: "-0.1px", lineHeight: 1.2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{top.batch.name}</div>
          <div style={{
            fontSize: 11, color: t.textSub, fontFamily: F, marginTop: 2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{getChannelLabel(top.channelId)}</div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 700,
          color: isOut ? "#FF453A" : "#FF9F0A",
          background: isOut ? "rgba(255,69,58,0.12)" : "rgba(255,159,10,0.12)",
          padding: "3px 9px", borderRadius: 8,
          fontVariantNumeric: "tabular-nums", flexShrink: 0, fontFamily: F,
        }}>
          {isOut ? "leer" : top.stock}
        </span>
      </div>
      {rest > 0 && (
        <div style={{ fontSize: 10, color: t.textMuted, fontFamily: F, marginTop: 6 }}>
          + {rest} {rest === 1 ? "weiterer Alarm" : "weitere Alarme"}
        </div>
      )}
    </>
  );
}

function GoalProgressWidget(props) {
  const { orders, goal, t, dark, F } = props;
  const sold = orders.filter(o => o.status !== "Retoure" && !o.archived).length;
  const pct = Math.min(100, (sold / Math.max(goal, 1)) * 100);
  const done = sold >= goal;
  return (
    <>
      <WidgetHeader icon={Icon.target} title="Verkaufsziel" t={t} F={F} accent={done ? t.accent.green.solid : t.accent.blue.solid} />
      <div>
        {/* "X / Y" inline — beide Zahlen sind klar als Bruch lesbar.
            Vorher war "X" gross + "von Y" klein → User las "X" als Ziel. */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 7 }}>
          <span style={{
            fontSize: 22, fontWeight: 700,
            color: done ? t.accent.green.solid : t.text,
            letterSpacing: "-0.5px", lineHeight: 1,
            fontVariantNumeric: "tabular-nums", fontFamily: F,
          }}>{sold}</span>
          <span style={{
            fontSize: 16, fontWeight: 500, color: t.textMuted,
            lineHeight: 1, letterSpacing: "-0.3px", fontFamily: F,
          }}>/</span>
          <span style={{
            fontSize: 16, fontWeight: 600, color: t.textSub,
            lineHeight: 1, letterSpacing: "-0.3px",
            fontVariantNumeric: "tabular-nums", fontFamily: F,
          }}>{goal}</span>
          <span style={{
            fontSize: 10, color: t.textMuted, marginLeft: "auto",
            letterSpacing: "0.04em", fontFamily: F,
          }}>{Math.round(pct)}%</span>
        </div>
        <div style={{ height: 5, borderRadius: 3, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: done ? "linear-gradient(90deg,#30D158,#5ee78a)" : "linear-gradient(90deg,#0A84FF,#5AC8FA)", transition: "width 0.6s cubic-bezier(0.34,1.2,0.64,1)" }} />
        </div>
      </div>
    </>
  );
}

function ChannelBreakdownWidget(props) {
  const { orders, getAllChannels, t, dark, F } = props;
  const since = Date.now() - 7 * 86400 * 1000;
  const recent = orders.filter(o => {
    if (o.status === "Retoure" || o.archived) return false;
    const dt = o.datum ? new Date(o.datum).getTime() : 0;
    return dt >= since;
  });
  const counts = {};
  recent.forEach(o => { counts[o.kanal] = (counts[o.kanal] || 0) + 1; });
  const PALETTE = ["#0A84FF","#FF9500","#30D158","#FF453A","#5E5CE6","#FF2D55","#64D2FF"];
  // Sortierung: count desc → grösster Kanal links, kleinster rechts.
  // Color-Map muss dem ORIGINAL-Index folgen (Kanal-Identität), nicht der Sortier-Position,
  // damit eBay konsistent dieselbe Farbe behält wenn andere Kanäle leer sind.
  const channelsOriginal = getAllChannels();
  const colorOf = {};
  channelsOriginal.forEach((c, i) => { colorOf[c.id] = PALETTE[i % PALETTE.length]; });
  const channels = channelsOriginal
    .slice()
    .sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0));
  const max = Math.max(...channels.map(c => counts[c.id] || 0), 1);
  return (
    <>
      <WidgetHeader icon={Icon.bars} title="Kanäle · 7T" t={t} F={F} accent={t.accent.blue.solid} />
      {/* Vertikale Balken nebeneinander — grösster links, kleinster rechts.
          Höhe der Balken proportional zum Max. Inaktive (count=0) als Stub. */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 64, marginTop: 2 }}>
        {channels.map((c) => {
          const v = counts[c.id] || 0;
          const h = v > 0 ? Math.max(6, (v / max) * 56) : 4;
          const isEmpty = v === 0;
          return (
            <div key={c.id} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              {/* Zahl über Balken — versteckt wenn 0 (zu viel Rauschen) */}
              <div style={{
                fontSize: 10, fontWeight: 700, color: t.text,
                fontVariantNumeric: "tabular-nums", fontFamily: F,
                lineHeight: 1, height: 12,
                opacity: isEmpty ? 0 : 1,
              }}>{v}</div>
              {/* Balken */}
              <div style={{
                width: "100%", height: `${h}px`,
                background: isEmpty
                  ? (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)")
                  : colorOf[c.id],
                borderRadius: "4px 4px 0 0",
                transition: "height 0.6s cubic-bezier(0.34,1.2,0.64,1)",
              }} />
            </div>
          );
        })}
      </div>
      {/* Labels unter den Balken */}
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        {channels.map(c => (
          <div key={c.id} style={{
            flex: 1, minWidth: 0, textAlign: "center",
            fontSize: 9, fontWeight: 500, color: t.textSub,
            fontFamily: F, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            letterSpacing: "-0.05px",
          }}>{c.name}</div>
        ))}
      </div>
    </>
  );
}

function QuickActionsWidget(props) {
  const { t, dark, F, can, onOpenScanner, onOpenLager, onOpenStats, onOpenAdd, onSwitchToOrders } = props;
  const ICON = {
    add: (
      <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
        <path d="M11 4V18M4 11H18" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/>
      </svg>
    ),
    scanner: (
      <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
        <rect x="3" y="6" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M7 6V4.5C7 3.7 7.7 3 8.5 3H13.5C14.3 3 15 3.7 15 4.5V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        <circle cx="11" cy="11.5" r="3" stroke="currentColor" strokeWidth="1.6"/>
      </svg>
    ),
    lager: (
      <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
        <path d="M3 7L11 3L19 7V15L11 19L3 15V7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
        <path d="M3 7L11 11M11 11L19 7M11 11V19" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      </svg>
    ),
    stats: (
      <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
        <path d="M4 17V11M11 17V5M18 17V13" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/>
      </svg>
    ),
  };
  const actions = [
    can?.("write")    && { id: "add",     label: "Neu",     icon: ICON.add,     accent: "#0A84FF", onClick: () => { onOpenAdd?.(); onSwitchToOrders?.(); } },
    can?.("write")    && { id: "scanner", label: "Scannen", icon: ICON.scanner, accent: "#BF5AF2", onClick: onOpenScanner },
    can?.("finance")  && { id: "lager",   label: "Lager",   icon: ICON.lager,   accent: "#FF9500", onClick: onOpenLager },
    can?.("finance")  && { id: "stats",   label: "Stats",   icon: ICON.stats,   accent: "#30D158", onClick: onOpenStats },
  ].filter(Boolean);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(4, actions.length)}, 1fr)`, gap: 6, width: "100%" }}>
      {actions.map(a => (
        <button
          key={a.id}
          onClick={(e) => { e.stopPropagation(); a.onClick?.(); }}
          style={{
            padding: "10px 4px",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.035)",
            border: `1px solid ${t.glassBorder}`, borderRadius: 14,
            cursor: "pointer", color: t.text, fontFamily: F,
            transition: "transform 0.15s ease, background 0.18s ease",
          }}
          onPointerDown={(e) => { e.currentTarget.style.transform = "scale(0.94)"; }}
          onPointerUp={(e) => { e.currentTarget.style.transform = ""; }}
          onPointerLeave={(e) => { e.currentTarget.style.transform = ""; }}
        >
          <span style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 30, height: 30, borderRadius: 9,
            background: `${a.accent}1F`, color: a.accent,
          }}>{a.icon}</span>
          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "-0.05px", color: t.textSub }}>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

// Stale orders — Bestellungen, die seit > 12h NICHT versendet wurden.
// Zeigt einen kritischen Warnhinweis. shouldRender filtert das Widget aus,
// wenn keine kritischen Orders vorliegen.
function StaleOrdersWidget(props) {
  const { orders, t, F, getStatusLabel, onSwitchToOrders } = props;
  const stale = computeStaleOrders(orders);
  const top3 = stale.slice(0, 3);
  return (
    <button onClick={(e) => { e.stopPropagation(); onSwitchToOrders?.(); haptic(10); }} style={{
      width: "100%", textAlign: "left",
      background: "transparent", border: "none", padding: 0,
      cursor: "pointer", fontFamily: F,
    }}>
      <WidgetHeader icon={Icon.warning} title="Kritisch · > 12h" t={t} F={F} accent={t.accent.red.solid} />
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: t.accent.red.solid, letterSpacing: "-0.7px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{stale.length}</span>
        <span style={{ fontSize: 11, color: t.textSub, fontWeight: 500 }}>schnellstmöglich versenden</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {top3.map(o => (
          <div key={o.id} style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
            <span style={{
              fontSize: 11, color: t.text, fontWeight: 500,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              flex: 1, minWidth: 0,
            }}>{o.name || `#${o.bestellnummer || "?"}`}</span>
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: t.accent.red.solid, background: t.accent.red.weak,
              padding: "1px 7px", borderRadius: 7,
              flexShrink: 0, fontVariantNumeric: "tabular-nums",
            }}>{formatHoursAgo(o.datum || o.createdAt)}</span>
          </div>
        ))}
        {stale.length > top3.length && (
          <div style={{ fontSize: 10, color: t.textMuted, marginTop: 1 }}>+ {stale.length - top3.length} weitere</div>
        )}
      </div>
    </button>
  );
}

// Findet das beste verfügbare Order-Datum für Age-Berechnung.
// Priorität: datum (vom User gepflegt) > createdAt (DB-Timestamp).
// Legacy-Orders ohne createdAt werden so trotzdem korrekt erkannt.
function bestOrderTimestamp(o) {
  if (o.datum) {
    const ts = new Date(o.datum + "T00:00:00").getTime();
    if (Number.isFinite(ts)) return ts;
  }
  if (o.createdAt) {
    const ts = new Date(o.createdAt).getTime();
    if (Number.isFinite(ts)) return ts;
  }
  return null;
}

// Hilfsfunktion: Findet alle "verspäteten" Bestellungen.
//
// Definition: Status "Offen" UND Datum liegt VOR heute (also gestern oder älter).
//
// Tag-basierte Logik (nicht Stunden-basiert) matcht das mentale User-Modell:
//   • Heute erstellte Order → nicht verspätet (egal zu welcher Uhrzeit)
//   • Gestern oder früher erstellte Order, noch immer Offen → verspätet
//
// String-Vergleich auf YYYY-MM-DD-Basis (lexikographisch). Beide Seiten basieren
// auf UTC-Date-Strings (via toISOString().slice(0,10)) — das matcht wie die
// App das datum-Feld bei neuen Orders setzt (siehe `today()` in app.jsx).
// Dadurch keine Timezone-Drift: ein "heute erstellter" Eintrag hat genau den
// gleichen Date-String wie unser todayStr → nicht stale.
//
// Vorher waren die Bugs:
//   • 12h-Cutoff: heute morgen erstellte Order zählte ab 12 Uhr stale
//   • Lokale Zeitvergleich vs UTC-stored datum: Timezone-Versatz von Stunden
//
// Bereit-Orders zählen nicht — Etikett ist drauf, Post-Trip ist normal.
// Plus: Dedup nach o.id als Sicherheitsnetz.
function computeStaleOrders(orders) {
  // UTC-Today (gleiche Quelle wie `today()` in app.jsx)
  const todayStr = new Date().toISOString().slice(0, 10);
  const seen = new Set();
  return (orders || [])
    .filter(o => !o.archived)
    .filter(o => o.status === "Offen")
    .filter(o => {
      // datum (User-gepflegt) hat Priorität — das ist was der User sieht.
      // Falls fehlend: createdAt-Datum-Anteil als Fallback (slice der ISO-String).
      let dayStr = null;
      if (typeof o.datum === "string" && /^\d{4}-\d{2}-\d{2}/.test(o.datum)) {
        dayStr = o.datum.slice(0, 10);
      } else if (typeof o.createdAt === "string" && /^\d{4}-\d{2}-\d{2}/.test(o.createdAt)) {
        dayStr = o.createdAt.slice(0, 10);
      }
      // Verspätet = Datum liegt strikt VOR heute.
      return dayStr !== null && dayStr < todayStr;
    })
    .filter(o => {
      if (seen.has(o.id)) return false;
      seen.add(o.id);
      return true;
    })
    .sort((a, b) => (bestOrderTimestamp(a) || 0) - (bestOrderTimestamp(b) || 0));
}

// "vor 14h", "vor 2T" etc. — kompakte Anzeige für die Stale-Pille.
// Akzeptiert sowohl ISO-Timestamps als auch reine Datum-Strings (YYYY-MM-DD).
function formatHoursAgo(iso) {
  if (!iso) return "—";
  // Falls reines Datum: an Tagesanfang aufrunden, sonst direkt parsen
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(iso));
  const ts = isDateOnly ? new Date(iso + "T00:00:00").getTime() : new Date(iso).getTime();
  if (!Number.isFinite(ts)) return "—";
  const ms = Math.max(0, Date.now() - ts);
  const hours = Math.floor(ms / 3600000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}T`;
}

// ─────────────────────────────────────────────────────────────
// Lager-Summary-Helper — extrahierte Logik die das Widget UND das
// Lager-Sheet teilen können. Nutzt die existierenden Channel-Finance-Helpers
// und die FIFO-Verteilung pro Batch.
//
// Returns: {
//   totalAmLager, totalStueckzahl, totalSold,
//   activeBatch, activeBatchSold, activeBatchRest,
//   wertImLager, avgPrice
// }
// ─────────────────────────────────────────────────────────────
function computeLagerSummary({ orders, batches, getActiveBatch, getReservedPools, getAllChannels, getChannelFinance }) {
  const allActiveOrders = (orders || []).filter(o => o.status !== "Retoure" && !o.archived);
  const totalSold       = allActiveOrders.length;
  const totalStueckzahl = (batches || []).reduce((s, b) => s + (b.stueckzahl || 0), 0);
  const totalAmLager    = Math.max(0, totalStueckzahl - totalSold);

  const activeBatch = typeof getActiveBatch === "function" ? getActiveBatch() : null;
  const activeBatchSold = activeBatch
    ? (orders || []).filter(o => o.batchId === activeBatch.id && o.status !== "Retoure" && !o.archived).length
    : 0;
  const activeBatchRest = activeBatch ? Math.max(0, (activeBatch.stueckzahl || 0) - activeBatchSold) : 0;

  // Gewichteter Durchschnitts-Preis aus Sales-Channels (nach Reservierungs-Anteil
  // im aktiven Batch). Identische Logik wie im Lager-Sheet damit Werte konsistent
  // bleiben. Bei einem Batch ohne Reservierung: erster Sales-Channel-Preis.
  let avgPrice = 0;
  try {
    const salesChannels = (typeof getAllChannels === "function" ? getAllChannels() : []) || [];
    if (activeBatch && salesChannels.length > 0 && getChannelFinance) {
      const reservierungen = activeBatch.reservierungen || {};
      const entries = salesChannels.map(c => ({ id: c.id, pool: reservierungen[c.id] || 0, fin: getChannelFinance(c.id) }));
      const sum = entries.reduce((s, e) => s + e.pool, 0);
      avgPrice = sum > 0
        ? entries.reduce((s, e) => s + e.pool * e.fin.price, 0) / sum
        : (entries[0]?.fin.price || 0);
    }
  } catch { /* defensiv: bei kaputten Daten avgPrice = 0 */ }

  // "Verschenkt" = Reservierungen in Reserved-Pools im aktiven Batch.
  // Verkaufbar = totalAmLager − Reserved-Pool-Reservierungen (über alle Batches).
  let restVerkaufbar = totalAmLager;
  try {
    if (typeof getReservedPools === "function") {
      const reservedIds = new Set(getReservedPools().map(c => c.id));
      const reservedSum = (batches || []).reduce((s, b) => {
        return s + Object.entries(b.reservierungen || {}).reduce(
          (ss, [k, v]) => ss + (reservedIds.has(k) ? (v || 0) : 0), 0);
      }, 0);
      restVerkaufbar = Math.max(0, totalAmLager - reservedSum);
    }
  } catch {}

  const wertImLager = restVerkaufbar * avgPrice;

  return {
    totalAmLager, totalStueckzahl, totalSold,
    activeBatch, activeBatchSold, activeBatchRest,
    avgPrice, wertImLager,
  };
}

// ─────────────────────────────────────────────────────────────
// Finance-Summary-Helper — gleiche Math wie Stats-Sheet, in einem
// reusable Helper ausgelagert damit Widget + Sheet konsistent bleiben.
//
// Returns: { verkauft, totalRev, totalVers, totalFees, totalCOGS,
//   nettoErloes, totalInvestment, cashStand, realNetto, marge }
// ─────────────────────────────────────────────────────────────
function computeFinanceSummary({ orders, batches, getOrderRevenue, getOrderShipping, getOrderFee, getBatchForOrder, getReservedPools }) {
  const COGS_FALLBACK = 0;
  // Reserved-Pool-IDs (Familie/Demo/Geschenk/…) — Orders auf diesen Channels
  // dürfen NIE als Verkauf zählen (defensiv, falls Daten kaputt sind oder
  // jemand manuell Orders auf einen Reserved-Channel routet).
  const reservedIds = new Set(
    typeof getReservedPools === "function"
      ? (getReservedPools() || []).map(c => c.id)
      : []
  );
  const sold = (orders || []).filter(o =>
    o.status !== "Retoure" && !o.archived && !reservedIds.has(o.kanal)
  );
  const verkauft = sold.length;
  const totalRev  = sold.reduce((s, o) => s + (getOrderRevenue?.(o)  || 0), 0);
  const totalVers = sold.reduce((s, o) => s + (getOrderShipping?.(o) || 0), 0);
  const totalFees = sold.reduce((s, o) => s + (getOrderFee?.(o)      || 0), 0);
  const totalCOGS = sold.reduce((s, o) => {
    const b = typeof getBatchForOrder === "function" ? getBatchForOrder(o) : null;
    return s + (b?.cogs || COGS_FALLBACK);
  }, 0);
  const totalInvestment = (batches || []).reduce((s, b) => s + (b.investment || 0), 0);
  const nettoErloes = totalRev - totalVers - totalFees;
  const cashStand   = nettoErloes - totalInvestment;
  const realNetto   = totalRev - totalVers - totalFees - totalCOGS;
  const marge       = totalRev > 0 ? (realNetto / totalRev) * 100 : 0;
  return {
    verkauft, totalRev, totalVers, totalFees, totalCOGS,
    nettoErloes, totalInvestment, cashStand, realNetto, marge,
  };
}

// ─────────────────────────────────────────────────────────────
// LagerSummaryWidget — Bestand + Wert + aktiver Batch.
// Click → Lager-Sheet.
// ─────────────────────────────────────────────────────────────
function LagerSummaryWidget(props) {
  const { orders, batches, t, F, fmt2, getActiveBatch, getReservedPools, getAllChannels, getChannelFinance } = props;
  const s = computeLagerSummary({ orders, batches, getActiveBatch, getReservedPools, getAllChannels, getChannelFinance });
  return (
    <>
      <WidgetHeader icon={Icon.box} title="Lager" t={t} F={F} accent={t.accent.orange.solid} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, width: "100%" }}>
        {/* Linke Spalte: Bestand */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 26, fontWeight: 700, color: t.text,
            letterSpacing: "-0.7px", lineHeight: 1, fontFamily: F,
            fontVariantNumeric: "tabular-nums",
          }}>{s.totalAmLager}</div>
          <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontFamily: F, lineHeight: 1.2 }}>
            Stück im Lager{s.totalStueckzahl > 0 ? ` · von ${s.totalStueckzahl}` : ""}
          </div>
        </div>
        {/* Rechte Spalte: aktiver Batch + Wert */}
        <div style={{ textAlign: "right", flexShrink: 0, minWidth: 0 }}>
          {s.activeBatch ? (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: F }}>
                Aktiver Batch
              </div>
              <div style={{
                fontSize: 12, fontWeight: 600, color: t.text, fontFamily: F,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                maxWidth: 140, marginTop: 2, letterSpacing: "-0.1px",
              }}>{s.activeBatch.name}</div>
              <div style={{ fontSize: 10, color: t.textSub, marginTop: 2, fontFamily: F, fontVariantNumeric: "tabular-nums" }}>
                {s.activeBatchRest}/{s.activeBatch.stueckzahl} frei
              </div>
              {s.wertImLager > 0 && (
                <div style={{
                  fontSize: 11, fontWeight: 600, color: t.accent.green.solid, marginTop: 5,
                  fontFamily: F, fontVariantNumeric: "tabular-nums",
                }}>~{fmt2(s.wertImLager)} €</div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 11, color: t.accent.red.solid, fontWeight: 600, fontFamily: F }}>
              Lager leer
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FinanceSummaryWidget — Cash-Stand + Marge + Umsatz.
// Click → Finanz-Sheet.
// ─────────────────────────────────────────────────────────────
function FinanceSummaryWidget(props) {
  const { orders, batches, t, F, fmt2, getOrderRevenue, getOrderShipping, getOrderFee, getBatchForOrder, getReservedPools } = props;
  const s = computeFinanceSummary({ orders, batches, getOrderRevenue, getOrderShipping, getOrderFee, getBatchForOrder, getReservedPools });
  const positive = s.cashStand >= 0;
  const cashColor = positive ? t.accent.green.solid : t.accent.orange.solid;
  return (
    <>
      <WidgetHeader icon={Icon.bars} title="Finanzen" t={t} F={F} accent={cashColor} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, width: "100%" }}>
        {/* Linke Spalte: Cash-Stand */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 24, fontWeight: 700, color: cashColor,
            letterSpacing: "-0.6px", lineHeight: 1, fontFamily: F,
            fontVariantNumeric: "tabular-nums",
          }}>{positive ? "+" : "−"}{fmt2(Math.abs(s.cashStand))} €</div>
          <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontFamily: F }}>Cash-Stand</div>
        </div>
        {/* Rechte Spalte: Marge + Umsatz */}
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: F }}>
            Marge
          </div>
          <div style={{
            fontSize: 14, fontWeight: 700, color: t.text, marginTop: 2, lineHeight: 1,
            fontVariantNumeric: "tabular-nums", fontFamily: F, letterSpacing: "-0.3px",
          }}>{s.marge.toFixed(1)} %</div>
          <div style={{
            fontSize: 11, color: t.textSub, marginTop: 5, fontFamily: F,
            fontVariantNumeric: "tabular-nums", letterSpacing: "-0.1px",
          }}>Umsatz {fmt2(s.totalRev)} €</div>
        </div>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════
// SwipeToDelete (P-Swipe-Delete)
// Apple-Style Swipe-Left-to-Delete mit Spring-Back, Velocity-Detection
// und nachträglichem Confirm-Dialog. Nutzt Pointer-Events + GPU-Transforms.
//
// Design-Entscheidungen:
//  • Lokaler State pro Card → bei Drag re-rendert nur diese Card,
//    nicht der Parent (List bleibt 60fps auch bei 50+ Items).
//  • State-Machine: idle | dragging | sweptOut | confirming | deleting | bouncing
//  • Velocity aus letzten ~120ms Pointer-Samples → "Schneller kurzer Swipe"
//    löst auch unter Distance-Threshold aus (User-Spec).
//  • "Eine Card zur Zeit": externer SwipeTracker-Ref hält die ID der aktuell
//    geöffneten Card. Andere Cards rasten zu, sobald die neue rauscht.
//
// Props:
//   id            — eindeutige ID (zur Tracker-Koordination)
//   tracker       — Ref-Objekt { current: { activeId, registry } } (vom Parent)
//   disabled      — Boolean (z.B. aktive Session)
//   disabledMessage — String, wird als Toast gezeigt
//   threshold     — Default 80px
//   velocityThreshold — Default 500 px/s
//   confirmTitle / confirmSubtitle / confirmWarning / confirmLabel
//   onConfirm     — async () → boolean (true = delete, false = cancel)
//                   ODER async () → void (Throw bei Error)
//   onDeleted     — () → void, wird nach Animation gerufen damit Parent
//                   die Card aus der Liste entfernen kann
//   showToast     — function (für disabled-Hinweis und Error-Message)
//   children      — Card-Inhalt (komplette Karte)
//   t / dark / F  — Theme
//   horizontal    — Boolean. Wenn true: zwingt touch-action: pan-x
//                   (für horizontale Container — typisch Online-Stores Quick-Access).
//                   In dem Fall handlen wir LEFT-only Swipe; rechte/keine
//                   Bewegung leiten wir an den Container durch.
// ═════════════════════════════════════════════════════════════
function SwipeToDelete({
  id, tracker,
  disabled, disabledMessage,
  threshold = 80, velocityThreshold = 500,
  confirmTitle, confirmSubtitle, confirmWarning, confirmLabel = "Löschen",
  onConfirm, onDeleted,
  showToast,
  children,
  t, dark, F,
  horizontal = false,
}) {
  const [state, setState] = useState("idle"); // idle | dragging | sweptOut | confirming | deleting | bouncing
  const [offset, setOffset] = useState(0);
  const cardRef = useRef(null);
  const wrapperRef = useRef(null);
  const dragRef = useRef({
    active: false,
    startX: 0, startY: 0,
    locked: null,    // null | "h" | "v" — Achse claimed nach kurzem Move
    samples: [],     // [{ t, x }] für Velocity-Berechnung
    pointerId: null,
    width: 0,
  });

  // Kapsel-Funktion für tracker-koordiniertes Schließen
  const closeOthers = () => {
    if (!tracker?.current) return;
    const reg = tracker.current.registry || {};
    for (const key in reg) {
      if (key !== id && reg[key]) reg[key]();
    }
    tracker.current.activeId = id;
  };
  // Beim Mount: in Tracker-Registry eintragen mit einer Reset-Funktion
  useEffect(() => {
    if (!tracker?.current) return;
    if (!tracker.current.registry) tracker.current.registry = {};
    tracker.current.registry[id] = () => {
      // Reset state für Card die vom Tracker geschlossen wird
      setState((curr) => (curr === "sweptOut" || curr === "confirming") ? "idle" : curr);
      setOffset(0);
    };
    return () => {
      if (tracker.current?.registry) delete tracker.current.registry[id];
      if (tracker.current?.activeId === id) tracker.current.activeId = null;
    };
    // eslint-disable-next-line
  }, [id]);

  // Bounce-Animation (für disabled): Card kurz nach links wackeln, zurück
  const triggerBounce = () => {
    setState("bouncing");
    // Schritt 1: -12px in 80ms
    setOffset(-12);
    setTimeout(() => {
      setOffset(0);
      setTimeout(() => setState("idle"), 180);
    }, 90);
    if (typeof haptic === "function") haptic(20);
    if (showToast && disabledMessage) showToast(disabledMessage, "info", 2400);
  };

  // Pointer-Handler
  const onPointerDown = (e) => {
    if (state === "deleting" || state === "confirming" || state === "sweptOut") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (disabled) {
      // Erst beim Drag triggern (nicht jeder Tap), aber Tap auf disabled blockt nicht.
      // Wir merken disabled-Status und prüfen im Move.
    }
    const w = cardRef.current?.getBoundingClientRect()?.width || 0;
    dragRef.current = {
      active: true,
      startX: e.clientX, startY: e.clientY,
      locked: null,
      samples: [{ t: performance.now(), x: e.clientX }],
      pointerId: e.pointerId,
      width: w,
    };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    // Achsen-Locking — innerhalb ersten 8px klären ob h/v
    if (!d.locked) {
      const total = Math.abs(dx) + Math.abs(dy);
      if (total < 8) return;
      if (Math.abs(dy) > Math.abs(dx) * 1.1) {
        d.locked = "v"; // vertikal scroll → Card lässt los
        d.active = false;
        return;
      }
      if (dx >= 0) {
        // rechts-Bewegung → in v1 ignorieren (Spec); falls horizontal-Container,
        // soll der Container scrollen können
        d.locked = "v";
        d.active = false;
        return;
      }
      d.locked = "h";
      // Pointer-Capture: alle weiteren Events kommen zu uns
      try { cardRef.current?.setPointerCapture?.(e.pointerId); } catch {}
      // Wenn disabled: Bounce statt Drag
      if (disabled) {
        d.active = false;
        triggerBounce();
        return;
      }
      closeOthers();
      setState("dragging");
    }
    if (d.locked !== "h" || !d.active) return;
    e.preventDefault?.(); // verhindert Container-Scroll während horizontal-Drag
    // Sample für Velocity
    d.samples.push({ t: performance.now(), x: e.clientX });
    if (d.samples.length > 6) d.samples.shift();
    // dx clamp: nicht über 0 hinaus, nicht weiter als -120% breite
    const max = -Math.max(d.width || 320, 320) * 1.2;
    const clamped = Math.min(0, Math.max(dx, max));
    setOffset(clamped);
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d.active && state !== "dragging") {
      // not-our drag (Achsen-Lock auf vertikal)
      return;
    }
    d.active = false;
    if (d.locked !== "h") return;
    try { cardRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
    // Velocity berechnen aus letzten Samples (~120ms-Window)
    const now = performance.now();
    const recent = d.samples.filter(s => now - s.t < 140);
    let velocity = 0;
    if (recent.length >= 2) {
      const dx = recent[recent.length - 1].x - recent[0].x;
      const dt = recent[recent.length - 1].t - recent[0].t;
      if (dt > 0) velocity = (dx / dt) * 1000; // px/s, negativ = links
    }
    const distance = Math.abs(offset);
    // Auslöse-Bedingung: Distance > threshold ODER (left-velocity > velocityThreshold UND distance > 12)
    const triggered = distance >= threshold || (-velocity > velocityThreshold && distance > 12);
    if (triggered) {
      // Slide-Out: full width nach links + Confirm-Dialog
      const w = d.width || 320;
      setState("sweptOut");
      setOffset(-w - 24); // ein bisschen Überschwingen
      // Confirm-Dialog leicht verzögert öffnen damit Animation sichtbar bleibt
      setTimeout(() => setState("confirming"), 200);
      if (typeof haptic === "function") haptic(15);
    } else {
      // Spring-Back zur Ausgangsposition
      setState("idle");
      setOffset(0);
    }
  };
  const onPointerCancel = (e) => {
    dragRef.current.active = false;
    if (state === "dragging") {
      setState("idle");
      setOffset(0);
    }
  };

  // Confirm-Dialog Aktionen
  const handleCancel = () => {
    setState("idle");
    setOffset(0);
  };
  const handleConfirm = async () => {
    setState("deleting");
    try {
      const result = await onConfirm();
      if (result === false) {
        // Caller hat abgebrochen (z.B. Server-Fehler) — zurück zu idle
        setState("idle");
        setOffset(0);
        return;
      }
      // Collapse-Animation läuft via CSS (max-height + opacity); danach Parent
      // kümmert sich um Listen-Removal.
      setTimeout(() => { onDeleted && onDeleted(); }, 280);
    } catch (e) {
      // Error: zurück zu idle, Toast vom Caller
      setState("idle");
      setOffset(0);
      if (showToast) showToast(e?.message || "Löschen fehlgeschlagen", "error");
    }
  };

  // Transition-CSS je nach State
  const transition = (() => {
    if (state === "dragging") return "none";
    if (state === "bouncing") return "transform 0.16s cubic-bezier(0.34, 1.56, 0.64, 1)";
    if (state === "sweptOut") return "transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)";
    if (state === "deleting") return "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
    // spring-back / idle: leichter Bouncy-Cubic
    return "transform 0.36s cubic-bezier(0.34, 1.45, 0.64, 1)";
  })();

  const isDeleting = state === "deleting";
  const cardHeight = isDeleting ? 0 : undefined;

  // Roter Lösch-Hintergrund — nur sichtbar wenn Offset != 0
  const showRedBg = offset < -2;
  const redIntensity = Math.min(1, Math.abs(offset) / Math.max(threshold, 60));

  return (
    <div ref={wrapperRef} style={{
      position: "relative", overflow: "hidden",
      maxHeight: isDeleting ? 0 : 600, // 600 ist großzügige Obergrenze
      opacity: isDeleting ? 0 : 1,
      transition: isDeleting ? "max-height 0.28s ease, opacity 0.16s ease, margin 0.28s ease, padding 0.28s ease" : "none",
      marginBottom: isDeleting ? 0 : undefined,
    }}>
      {/* Roter Lösch-Hintergrund — liegt unter der Card */}
      {showRedBg && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          background: t.accent.red.solid,
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          paddingRight: 22,
          opacity: redIntensity,
          borderRadius: 12,
          color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 14,
          gap: 6,
          letterSpacing: "0.02em",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6"/>
          </svg>
          {redIntensity > 0.55 ? "Loslassen zum Löschen" : "Löschen"}
        </div>
      )}
      {/* Eigentliche Card mit Transform */}
      <div
        ref={cardRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{
          position: "relative", zIndex: 1,
          transform: `translate3d(${offset}px, 0, 0)`,
          transition,
          willChange: state === "dragging" ? "transform" : "auto",
          touchAction: horizontal ? "pan-x" : "pan-y",
        }}
      >
        {children}
      </div>

      {state === "confirming" && (
        <SwipeConfirmSheet
          dark={dark} t={t}
          title={confirmTitle}
          subtitle={confirmSubtitle}
          warning={confirmWarning}
          confirmLabel={confirmLabel}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}

// SwipeConfirmSheet — kleines bottom-sheet für die Lösch-Bestätigung
function SwipeConfirmSheet({ dark, t, title, subtitle, warning, confirmLabel, onCancel, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  return (
    <>
      <div onClick={onCancel} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.2s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={{
        position: "fixed", left: "50%", top: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(380px, calc(100vw - 32px))",
        background: dark ? "#1c1c1e" : "#fff",
        borderRadius: 22, padding: "22px 22px 18px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        zIndex: 97,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif",
        animation: "swipeConfirmIn 0.24s cubic-bezier(0.34, 1.45, 0.64, 1) both",
      }}>
        <style>{`@keyframes swipeConfirmIn { from { opacity: 0; transform: translate(-50%, -46%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }`}</style>
        <div style={{ fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 4, textAlign: "center", lineHeight: 1.3 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: t.textSub, marginBottom: 10, textAlign: "center" }}>{subtitle}</div>}
        {warning && (
          <div style={{
            background: t.accent.red.weak, color: t.accent.red.solid,
            borderRadius: 10, padding: "10px 12px", marginBottom: 14,
            fontSize: 12, lineHeight: 1.5, fontWeight: 500,
          }}>{warning}</div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "12px",
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
            border: "none", borderRadius: 12,
            color: t.text, fontWeight: 600, fontSize: 14,
            cursor: "pointer",
          }}>Abbrechen</button>
          <button onClick={async () => { setSubmitting(true); await onConfirm(); setSubmitting(false); }} disabled={submitting} style={{
            flex: 1, padding: "12px",
            background: t.accent.red.solid, color: "#fff",
            border: "none", borderRadius: 12,
            fontWeight: 700, fontSize: 14,
            cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.6 : 1,
          }}>{submitting ? "…" : confirmLabel}</button>
        </div>
      </div>
    </>
  );
}

// (Landing-Widgets wurden in P-Landing-Redesign entfernt — die neue
// Landingpage rendert reines Typografie-Layout direkt in app.jsx,
// kein Widget-Grid mehr. Bestellungen-Dashboard nutzt DashboardArea
// + WIDGETS unverändert weiter.)
// ═════════════════════════════════════════════════════════════

function RecentActivityWidget(props) {
  const { auditEntries, t, F, describeAuditEntry, formatRelative } = props;
  const top = (auditEntries || []).slice(0, 4);
  return (
    <>
      <WidgetHeader icon={Icon.clock} title="Aktivität" t={t} F={F} accent={t.accent.blue.solid} />
      {top.length === 0 ? (
        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: F, paddingTop: 2 }}>Noch nichts geschehen</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {top.map(e => {
            const desc = describeAuditEntry(e);
            return (
              <div key={e.id} style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
                <span style={{ fontSize: 12, lineHeight: 1.3, opacity: 0.85 }}>{desc.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: t.text, fontWeight: 500, fontFamily: F, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.25 }}>{desc.text}</div>
                  <div style={{ fontSize: 9, color: t.textMuted, fontFamily: F, marginTop: 1 }}>{formatRelative(e.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

const WIDGETS = {
  today_summary: {
    id: "today_summary", title: "Heute", icon: "☀️",
    defaultSize: "large", sizes: ["medium","large"],
    visibleFor: ["owner","admin","fulfillment","finance","viewer"],
    component: TodaySummaryWidget,
    variant: "hero",
    navigateTo: null,
  },
  quick_actions: {
    id: "quick_actions", title: "Schnellzugriff", icon: "⚡",
    defaultSize: "large", sizes: ["medium","large"],
    visibleFor: ["owner","admin","fulfillment","finance"],
    component: QuickActionsWidget,
    variant: "compact",
    navigateTo: null,
  },
  bereit_count: {
    id: "bereit_count", title: "Bereit-Anzahl", icon: "📦",
    defaultSize: "small", sizes: ["small"],
    visibleFor: ["owner","admin","fulfillment"],
    component: BereitCountWidget,
    navigateTo: "orders_bereit",
  },
  // stale_orders Widget wurde in das Today-Hero integriert (User-Wunsch:
  // dezent aber prominent, keine eigene Kachel mehr). Die Logik
  // (computeStaleOrders) bleibt erhalten und wird vom Today-Widget genutzt.
  reorder_alarms: {
    id: "reorder_alarms", title: "Nachbestellen", icon: "⚠️",
    defaultSize: "medium", sizes: ["medium"],
    visibleFor: ["owner","admin","fulfillment","finance"],
    component: ReorderAlarmsWidget,
    navigateTo: "lager",
    shouldRender: (props) => (props?.alarms?.length || 0) > 0,
  },
  goal_progress: {
    id: "goal_progress", title: "Verkaufsziel", icon: "🎯",
    defaultSize: "small", sizes: ["small","medium"],
    visibleFor: ["owner","admin","finance","viewer"],
    component: GoalProgressWidget,
    // Klick öffnet das Goal-Edit-Popup direkt (User-Wunsch — nicht zum Stats-Tab).
    navigateTo: "goal_edit",
  },
  inventory_summary: {
    id: "inventory_summary", title: "Lager", icon: "📦",
    defaultSize: "medium", sizes: ["medium"],
    visibleFor: ["owner","admin","fulfillment"],
    component: LagerSummaryWidget,
    navigateTo: "lager",
  },
  finance_summary: {
    id: "finance_summary", title: "Finanzen", icon: "💰",
    defaultSize: "medium", sizes: ["medium"],
    visibleFor: ["owner","admin","finance"],
    component: FinanceSummaryWidget,
    navigateTo: "stats",
    // Bei 0 Verkäufen kein sinnvoller Cash-Stand → ausblenden.
    shouldRender: (props) => (props?.orders || []).filter(o => o.status !== "Retoure" && !o.archived).length > 0,
  },
  channel_breakdown: {
    id: "channel_breakdown", title: "Kanäle · 7T", icon: "📊",
    defaultSize: "medium", sizes: ["medium","large"],
    visibleFor: ["owner","admin","finance"],
    component: ChannelBreakdownWidget,
    navigateTo: "stats",
  },
  recent_activity: {
    id: "recent_activity", title: "Aktivität", icon: "🕒",
    defaultSize: "medium", sizes: ["medium","large"],
    visibleFor: ["owner","admin"],
    component: RecentActivityWidget,
    navigateTo: "audit",
  },

};

// ─────────────────────────────────────────────────────────────
// DashboardArea — renders the widget grid for both mobile (overlay)
// and desktop (inline). Long-press → wiggle edit-mode + DnD reorder.
// ─────────────────────────────────────────────────────────────
function DashboardArea({
  isMobile, isTablet, isDesktop, dark, t, F,
  currentStore,
  getVisibleWidgets,
  dashboardEditMode, setDashboardEditMode,
  setShowCustomizeDashboard,
  setShowSettings,
  storeDashboardLayout,
  saveDashboardLayout,
  defaultLayoutForRole,
  handleWidgetNavigate,
  haptic: hapticProp,
  widgetProps,
  setCurrentView,
  glass,
  loaded,
  onPullRefresh,
  // Optional Header-Overrides (für Landing-Dashboard, P-Landing-Refactor):
  // title       — überschreibt das Time-of-Day-Greeting
  // subtitle    — überschreibt currentStore.name
  // hideViewSwitcher — versteckt den Desktop-Tab-Switch (Dashboard/Bestellungen)
  title,
  subtitle,
  hideViewSwitcher = false,
}) {
  const hap = hapticProp || haptic;
  const glassFn = glass || ((extra = {}) => ({
    background: t?.glass || (dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)"),
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
    border: `1px solid ${t?.glassBorder || "rgba(0,0,0,0.06)"}`,
    boxShadow: t?.glassShadow || "0 1px 3px rgba(0,0,0,0.05)",
    ...extra,
  }));
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const longPressTimer = useRef(null);
  const pressStart = useRef(null);
  const widgetRefs = useRef({});

  const ptr = usePullToRefresh(!isDesktop ? onPullRefresh : null);

  const visible = getVisibleWidgets();
  // iOS-Style 2-Spalten-Grid auf Mobile (User-Wunsch).
  // Kleine Widgets (size="small") nehmen 1 Spalte, mittlere/große die volle Breite.
  const cols = isMobile ? 2 : isTablet ? 2 : 4;
  const spanFor = (size) => {
    const s = size === "small" ? 1 : size === "medium" ? 2 : 4;
    return `span ${Math.min(s, cols)}`;
  };

  const exitEditMode = () => {
    setDashboardEditMode(false);
    setDraggingId(null);
    setDragOverId(null);
  };

  const handlePointerDown = (e, widgetId) => {
    if (dashboardEditMode) return;
    pressStart.current = { x: e.clientX, y: e.clientY };
    longPressTimer.current = setTimeout(() => {
      hap(20);
      setDashboardEditMode(true);
    }, 450);
  };
  const handlePointerMove = (e) => {
    if (!longPressTimer.current || !pressStart.current) return;
    const dx = e.clientX - pressStart.current.x;
    const dy = e.clientY - pressStart.current.y;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handlePointerUp = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    pressStart.current = null;
  };

  const onDragStart = (id) => {
    if (!dashboardEditMode) return;
    hap(10);
    setDraggingId(id);
  };
  const onDragOver = (id) => {
    if (!dashboardEditMode || !draggingId || draggingId === id) return;
    setDragOverId(id);
  };
  const onDragEnd = () => {
    if (draggingId && dragOverId && draggingId !== dragOverId) {
      const layout = storeDashboardLayout || defaultLayoutForRole();
      const arr = layout.widgets.slice();
      const fromIdx = arr.findIndex(w => w.id === draggingId);
      const toIdx   = arr.findIndex(w => w.id === dragOverId);
      if (fromIdx >= 0 && toIdx >= 0) {
        const [moved] = arr.splice(fromIdx, 1);
        arr.splice(toIdx, 0, moved);
        saveDashboardLayout({ ...layout, widgets: arr });
        hap(15);
      }
    }
    setDraggingId(null);
    setDragOverId(null);
  };

  const containerStyle = isDesktop ? {
    padding: "16px 24px 36px",
    fontFamily: F,
    minHeight: "calc(100vh - 80px)",
  } : {
    position: "fixed", inset: 0,
    background: t.bg,
    overflowY: "auto",
    paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 90px)",
    zIndex: 30,
    fontFamily: F,
  };

  // Wenn der User pulled, schiebt sich auch der Inhalt selbst nach unten (nicht
  // nur der kleine Pfeil-Indikator). Das Gefühl wird nativer und der User sieht
  // klar dass die Aktion etwas auslöst. Während Refresh: rastet auf threshold ein.
  const ptrTransform = ptr.refreshing
    ? `translateY(${70}px)`
    : ptr.pullDistance > 0 ? `translateY(${ptr.pullDistance}px)` : "translateY(0)";

  return (
    <div ref={!isDesktop ? ptr.containerRef : null} style={{
      ...containerStyle,
      position: !isDesktop ? "fixed" : "relative",
      WebkitOverflowScrolling: "touch",
    }}>
      <PullToRefreshIndicator pullDistance={ptr.pullDistance} refreshing={ptr.refreshing} t={t} />
      <style>{`
        @keyframes widgetIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes wiggle{
          0%,100% { transform: rotate(-0.55deg); }
          50%     { transform: rotate( 0.55deg); }
        }
        .widget-edit { animation: wiggle 0.18s ease-in-out infinite alternate; }
        .widget-edit > * { pointer-events: none; }
        .widget-drop-target { outline: 2px dashed #0A84FF; outline-offset: -3px; }
      `}</style>
      {/* Content-Wrapper — bewegt sich beim Pull mit nach unten */}
      <div style={{
        transform: !isDesktop ? ptrTransform : "none",
        transition: ptr.refreshing || ptr.pullDistance === 0 ? "transform 0.2s cubic-bezier(0.34,1.2,0.64,1)" : "none",
        willChange: !isDesktop ? "transform" : "auto",
      }}>

      {isDesktop && !hideViewSwitcher && (
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {[
            { id: "dashboard", label: "Dashboard" },
            { id: "orders",    label: "Bestellungen" },
          ].map(item => {
            const active = item.id === "dashboard";
            return (
              <button
                key={item.id}
                onClick={() => { hap(6); setCurrentView(item.id); }}
                style={{
                  padding: "8px 16px", border: "none", borderRadius: 12,
                  background: active ? t.pillActive : "transparent",
                  color: active ? t.text : t.textMuted,
                  fontWeight: active ? 600 : 500, fontSize: 13, letterSpacing: "-0.1px",
                  fontFamily: F, cursor: "pointer",
                  transition: "all 0.18s ease",
                  boxShadow: active ? `0 1px 8px rgba(0,0,0,${dark?0.3:0.06})` : "none",
                }}
              >{item.label}</button>
            );
          })}
        </div>
      )}

      <div style={{
        padding: isDesktop ? "0 0 14px" : "calc(env(safe-area-inset-top, 0px) + 16px) 16px 10px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: isDesktop ? 32 : 26, fontWeight: 700, color: t.text, letterSpacing: isDesktop ? "-0.8px" : "-0.6px", lineHeight: 1.1 }}>
            {title || (() => {
              const h = new Date().getHours();
              return h < 5 ? "Gute Nacht" : h < 11 ? "Guten Morgen" : h < 18 ? "Guten Tag" : "Guten Abend";
            })()}
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {(subtitle != null ? subtitle : currentStore?.name) || ""}{dashboardEditMode ? " · Bearbeiten" : ""}
          </div>
        </div>
        {dashboardEditMode ? (
          <button
            onClick={exitEditMode}
            style={{
              padding: "8px 18px", borderRadius: 14,
              background: "#0A84FF", color: "#fff",
              fontFamily: F, fontSize: 14, fontWeight: 700,
              border: "none", cursor: "pointer",
              boxShadow: "0 4px 18px rgba(10,132,255,0.4)",
            }}
          >Fertig</button>
        ) : (
          // Einstellungen-Button — vorher 3-Punkte-Menü, jetzt Zahnrad.
          // Öffnet die Settings (Dashboard-anpassen liegt in Settings → Anzeige).
          <button
            onClick={() => { if (setShowSettings) setShowSettings(true); hap(8); }}
            aria-label="Einstellungen"
            style={{
              ...glassFn({ borderRadius: 50 }),
              width: 40, height: 40, padding: 0,
              border: `1px solid ${t.glassBorder}`, cursor: "pointer", color: t.text,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {Icon.gear(18)}
          </button>
        )}
      </div>

      {dashboardEditMode && (
        <div style={{
          padding: "10px 14px", marginBottom: 14,
          background: "rgba(10,132,255,0.1)", border: "1px solid rgba(10,132,255,0.3)",
          borderRadius: 12, fontSize: 12, color: "#0A84FF", fontWeight: 600, fontFamily: F,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span>✨</span>
          <span>Halte ein Widget gedrückt und ziehe es, um die Reihenfolge zu ändern</span>
        </div>
      )}

      {!loaded ? (
        <div style={{
          padding: isDesktop ? "0" : "0 12px 12px",
          display: "grid",
          gridTemplateColumns: cols === 1 ? "1fr" : `repeat(${cols}, 1fr)`,
          gap: 9,
        }}>
          <div style={{ gridColumn: cols === 1 ? "1" : `span ${cols}` }}>
            <SkeletonWidget t={t} dark={dark} glass={glassFn} hero />
          </div>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ gridColumn: cols === 1 ? "1" : (i < 2 ? "span 1" : `span ${Math.min(2, cols)}`) }}>
              <SkeletonWidget t={t} dark={dark} glass={glassFn} />
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div style={{ padding: "60px 20px", textAlign: "center", color: t.textMuted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🏠</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: t.text, marginBottom: 4 }}>Dein Dashboard ist leer</div>
          <div style={{ fontSize: 12 }}>Tippe oben rechts um Widgets zu aktivieren.</div>
        </div>
      ) : (
        <div style={{
          padding: isDesktop ? "0" : "0 12px 12px",
          display: "grid",
          gridTemplateColumns: cols === 1 ? "1fr" : `repeat(${cols}, 1fr)`,
          gap: 9,
          transition: "all 0.3s ease",
        }}
          onPointerMove={handlePointerMove}
        >
          {visible.map((wConfig, i) => {
            const def = WIDGETS[wConfig.id];
            if (!def) return null;
            const Comp = def.component;
            const isDragging = draggingId === wConfig.id;
            const isDropTarget = dragOverId === wConfig.id && draggingId && draggingId !== wConfig.id;
            const onClick = (!dashboardEditMode && def.navigateTo) ? () => handleWidgetNavigate(def.navigateTo) : null;
            return (
              <div
                key={wConfig.id}
                ref={el => { if (el) widgetRefs.current[wConfig.id] = el; }}
                draggable={dashboardEditMode}
                onDragStart={(e) => { if (dashboardEditMode) { e.dataTransfer.setData("text/plain", wConfig.id); e.dataTransfer.effectAllowed = "move"; onDragStart(wConfig.id); } }}
                onDragOver={(e) => { if (dashboardEditMode) { e.preventDefault(); onDragOver(wConfig.id); } }}
                onDragEnd={onDragEnd}
                onDrop={(e) => { e.preventDefault(); onDragEnd(); }}
                onPointerDown={(e) => handlePointerDown(e, wConfig.id)}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                className={`${dashboardEditMode ? "widget-edit" : ""} ${isDropTarget ? "widget-drop-target" : ""}`}
                style={{
                  gridColumn: spanFor(wConfig.size),
                  position: "relative",
                  opacity: isDragging ? 0.4 : 1,
                  transition: "opacity 0.15s ease",
                  animationDelay: dashboardEditMode ? `${(i % 3) * -0.04}s` : "0s",
                }}
              >
                <WidgetCard
                  onClick={onClick}
                  t={t} dark={dark} F={F} glass={glassFn}
                  size={wConfig.size}
                  variant={def.variant}
                  gridSpan="auto"
                  animationDelay={dashboardEditMode ? 0 : i * 0.04}
                >
                  <Comp size={wConfig.size} {...widgetProps} />
                </WidgetCard>

                {dashboardEditMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const layout = storeDashboardLayout || defaultLayoutForRole();
                      saveDashboardLayout({
                        ...layout,
                        widgets: layout.widgets.map(w => w.id === wConfig.id ? { ...w, visible: false } : w),
                      });
                      hap(8);
                    }}
                    style={{
                      position: "absolute", top: -8, left: -8,
                      width: 26, height: 26, borderRadius: 13,
                      background: "#FF453A", color: "#fff", border: "none",
                      cursor: "pointer", fontWeight: 700, fontSize: 14,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                      pointerEvents: "auto", zIndex: 2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                    title="Widget vom Dashboard entfernen"
                  >−</button>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>{/* /Content-Wrapper für PTR-Translate */}
    </div>
  );
}

// Default layout per role — used when dashboard_layout is null.
// Reihenfolge: Heute (Hero) → Schnellzugriff → restliche Widgets.
function defaultLayoutForRole(role) {
  const LAYOUTS = {
    owner:       ["today_summary","quick_actions","bereit_count","goal_progress","inventory_summary","finance_summary","reorder_alarms","channel_breakdown","recent_activity"],
    admin:       ["today_summary","quick_actions","bereit_count","goal_progress","inventory_summary","finance_summary","reorder_alarms","channel_breakdown","recent_activity"],
    fulfillment: ["today_summary","quick_actions","bereit_count","inventory_summary","reorder_alarms"],
    finance:     ["today_summary","quick_actions","goal_progress","finance_summary","channel_breakdown","reorder_alarms"],
    viewer:      ["today_summary","goal_progress"],
  };
  const ids = LAYOUTS[role] || ["today_summary"];
  return {
    version: 1,
    widgets: ids.map(id => ({
      id,
      size: WIDGETS[id]?.defaultSize || "medium",
      visible: true,
    })),
  };
}

// ─────────────────────────────────────────────────────────────
// useValidation — zentrale Pflichtfeld-Validierung mit Shake + Scroll + Fokus
//
// Usage:
//   const { validate, errors, clearError, registerRef } = useValidation({
//     name:  { required: true },
//     price: { required: true, type: "price" },  // > 0
//     stock: { required: true, type: "stock" },  // integer >= 0
//   });
//
//   const ok = validate({ name, price, stock });
//   // Im JSX: <div ref={registerRef('name')} style={{ border: errors.name ? '...' : ... }}>
//   //         {errors.name && <span>…</span>}
// ─────────────────────────────────────────────────────────────
function useValidation(schema) {
  const [errors, setErrors] = useState({});
  const fieldRefs = useRef({});

  // Gibt ein Ref-Callback zurück der auf dem Wrapper-Div sitzt
  const registerRef = (field) => (el) => {
    if (el) fieldRefs.current[field] = el;
    else delete fieldRefs.current[field];
  };

  const validate = (values) => {
    const newErrors = {};
    let firstField = null;

    for (const [field, rules] of Object.entries(schema)) {
      const raw = values[field];
      const str = (raw === null || raw === undefined) ? "" : String(raw);
      let error = null;

      // 1. Pflichtfeld-Check (Vorrang)
      if (rules.required && !str.trim()) {
        error = "Pflichtfeld";
      }

      // 2. Format-Validierung (nur wenn nicht leer + kein Pflichtfehler)
      if (!error && str.trim()) {
        if (rules.type === "price") {
          const n = parseFloat(str.replace(",", "."));
          if (!Number.isFinite(n) || n <= 0) error = "Preis muss größer als 0 sein";
        } else if (rules.type === "price_gte0") {
          const n = parseFloat(str.replace(",", "."));
          if (!Number.isFinite(n) || n < 0) error = "Preis ungültig";
        } else if (rules.type === "stock") {
          const n = parseInt(str.trim(), 10);
          if (isNaN(n) || n < 0 || !Number.isInteger(n)) error = "Lager-Anzahl muss ganzzahlig sein";
        } else if (rules.type === "email") {
          if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(str.trim())) error = "Bitte gültige Email-Adresse eingeben";
        }
      }

      if (error) {
        newErrors[field] = error;
        if (!firstField) firstField = field;
      }
    }

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;

    if (hasErrors) {
      // Haptic warning
      try { navigator.vibrate && navigator.vibrate([10, 30, 10]); } catch {}

      // Shake alle Fehler-Felder via Web Animations API
      for (const field of Object.keys(newErrors)) {
        const el = fieldRefs.current[field];
        if (el?.animate) {
          el.animate(
            [
              { transform: "translateX(0)" },
              { transform: "translateX(-6px)" },
              { transform: "translateX(6px)" },
              { transform: "translateX(-4px)" },
              { transform: "translateX(4px)" },
              { transform: "translateX(0)" },
            ],
            { duration: 300, easing: "ease-in-out" }
          );
        }
      }

      // Scroll + Focus zum ersten Fehler-Feld
      if (firstField) {
        const el = fieldRefs.current[firstField];
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ block: "nearest", behavior: "smooth" });
            const input = el.querySelector("input,textarea,select");
            if (input) setTimeout(() => input.focus(), 180);
          }, 40);
        }
      }
    }

    return !hasErrors;
  };

  const clearError = (field) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  return { validate, errors, clearError, registerRef };
}

// ─────────────────────────────────────────────────────────────
// Shared Utilities (Cluster-C — dedupliziert aus flea.jsx + market.jsx)
// ─────────────────────────────────────────────────────────────

/** Formatiert einen Betrag als Deutsche-Locale-Euro-String, z.B. "12,50 €". */
const fmtCurrency = (n) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};

/** Fetch-Wrapper mit Bearer-Auth-Header. Wirft bei HTTP-Fehler. */
async function apiCall(authToken, path, opts = {}) {
  const { method = "GET", body } = opts;
  const headers = { "Authorization": `Bearer ${authToken}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(path, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  if (!res.ok) {
    const err = await res.text().catch(() => "");
    throw new Error(`API ${method} ${path} → ${res.status}: ${err.slice(0, 200)}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

/**
 * Basisstatistik aus einem Sales-Array (funktioniert für Trödel + Standverkauf).
 * Gibt { active, count, bundleCount, total, cash, card } zurück.
 * Modusspezifische Felder (wishSum, discount etc.) werden in den
 * aufrufenden computeSessionStats / computeMarketStats ergänzt.
 */
function computeBaseStats(sales) {
  const active = (sales || []).filter(s => !s.is_voided);
  const total  = active.reduce((s, x) => s + Number(x.total || 0), 0);
  const cash   = active.filter(x => x.payment === "cash").reduce((s, x) => s + Number(x.total || 0), 0);
  const card   = active.filter(x => x.payment === "card").reduce((s, x) => s + Number(x.total || 0), 0);
  return { active, count: active.length, bundleCount: active.filter(x => x.is_bundle).length, total, cash, card };
}

// ─────────────────────────────────────────────────────────────
// ConfirmSheet — generisches Bestätigungs-Modal (Apple HIG)
// Confirm ist primärer Button (oben, ausgefüllt), Cancel sekundär (unten, Text).
// Bei confirmDestructive=true: Confirm-Button rot.
// useModalViewport + modalShellStyle kommen aus modal-utils.jsx (importiert oben).
// ─────────────────────────────────────────────────────────────
function ConfirmSheet({ dark, t, title, subtitle, warning, confirmLabel, confirmDestructive, onCancel, onConfirm }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [submitting, setSubmitting] = useState(false);
  const accent = confirmDestructive ? t.accent.red : t.accent.blue;
  return (
    <>
      <div onClick={onCancel} style={{
        position: "fixed", inset: 0, zIndex: 94,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 360, radius: 24, padding: "22px 22px 18px", zIndex: 95 })}>
        <div style={{ fontSize: 17, fontWeight: 700, color: t.text, marginBottom: 4, textAlign: "center" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: t.textSub, marginBottom: 12, textAlign: "center" }}>{subtitle}</div>}
        {warning && (
          <div style={{ background: accent.weak, color: accent.solid, borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12, lineHeight: 1.5, fontWeight: 500 }}>
            {warning}
          </div>
        )}
        <button onClick={async () => { setSubmitting(true); await onConfirm(); setSubmitting(false); }} disabled={submitting} style={{
          width: "100%", padding: "13px",
          background: accent.solid, color: "#fff",
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
          marginBottom: 8,
        }}>{submitting ? "…" : (confirmLabel || "Bestätigen")}</button>
        <button onClick={onCancel} style={{
          width: "100%", padding: "10px",
          background: "transparent", border: "none",
          color: t.textMuted,
          fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Abbrechen</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ActionMenu — Bottom-Menü für Drei-Punkte-Aktionen
// accentColor: t.accent.purple (Trödel), t.accent.green (Markt)
// ─────────────────────────────────────────────────────────────
function ActionMenu({ dark, t, onClose, actions, accentColor }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const ac = accentColor || t.accent.blue;
  const renderIcon = (key) => {
    if (key === "add")  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 2v10M2 7h10" strokeLinecap="round"/></svg>;
    if (key === "list") return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="3" width="10" height="2" rx="1"/><rect x="2" y="6" width="10" height="2" rx="1"/><rect x="2" y="9" width="10" height="2" rx="1"/></svg>;
    if (key === "note") return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 1h6l3 3v9H3V1z"/><path d="M9 1v3h3"/></svg>;
    return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="8" height="8" rx="1"/></svg>;
  };
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 320, radius: 20, padding: "10px 8px", zIndex: 93 })}>
        {actions.map((a, i) => (
          <button key={i} onClick={a.onClick} style={{
            width: "100%", padding: "13px 14px",
            background: "transparent", border: "none",
            color: a.destructive ? t.accent.red.solid : t.text,
            fontFamily: F, fontWeight: 600, fontSize: 14,
            cursor: "pointer", textAlign: "left",
            display: "flex", alignItems: "center", gap: 12,
            borderTop: i > 0 ? `0.5px solid ${t.divider}` : "none",
          }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7, flexShrink: 0,
              background: a.destructive ? t.accent.red.weak : ac.weak,
              color: a.destructive ? t.accent.red.solid : ac.solid,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {renderIcon(a.icon)}
            </div>
            {a.label}
          </button>
        ))}
      </div>
    </>
  );
}

// ES-Module Exports
export {
  // Hooks + Indicators
  usePullToRefresh, PullToRefreshIndicator,
  // Skeleton family
  Skeleton, SkeletonOrderRow, SkeletonWidget,
  SkeletonText, SkeletonListItem, useDelayedSkeleton,
  // BottomSheet
  BottomSheet,
  // Widget-Building-Blocks
  WidgetCard, WidgetHeader,
  // Widgets
  TodaySummaryWidget, BereitCountWidget, ReorderAlarmsWidget, GoalProgressWidget,
  ChannelBreakdownWidget, QuickActionsWidget, RecentActivityWidget, StaleOrdersWidget,
  LagerSummaryWidget, FinanceSummaryWidget,
  computeStaleOrders, formatHoursAgo,
  computeLagerSummary, computeFinanceSummary,
  // Map + Layout + Container
  WIDGETS, DashboardArea, defaultLayoutForRole,
  // Swipe-to-Delete
  SwipeToDelete,
  // Validation
  useValidation,
  // Shared Utilities
  fmtCurrency, apiCall, computeBaseStats,
  // Shared Modals
  ConfirmSheet, ActionMenu,
};
